/**
 * WHICH characters delimit an identifier, open a string literal, or are just
 * ordinary text — one row per engine, for the masker in `sqlTextMasker.ts`.
 * That module re-exports this one's public names, so callers import only it.
 *
 * CROSS-ENGINE PARITY — the RULE is identical on every engine: mask string
 * literals and comments with equal-length runs of spaces, copy delimited
 * identifiers through as opaque spans. Only the TABLE below differs, and only
 * because the very same characters genuinely mean different things in
 * different grammars. This module is that table plus the one pure helper that
 * reads it (`policyFor`); the RULE that consumes it lives in
 * `sqlTextMasker.ts`, the span scanners in `sqlStringSpans.ts`, and the MySQL
 * `sql_mode` derivations that flip two of its cells in `mysqlSqlMode.ts`:
 *
 *   engine  | `[…]`      | `"…"`      | `` `…` ``  | `$tag$…$tag$` | `q'X…X'`
 *   --------+------------+------------+------------+---------------+---------
 *   mssql   | identifier | identifier | ordinary   | ordinary      | ordinary
 *   pgsql   | ordinary   | identifier | ordinary   | STRING        | ordinary
 *   mysql   | ordinary   | STRING (1) | identifier | ordinary      | ordinary
 *   oracle  | ordinary   | identifier | ordinary   | ordinary      | STRING
 *   sqlite  | identifier | identifier | identifier | ordinary      | ordinary
 *   kusto   | ordinary   | STRING     | ordinary   | ordinary      | ordinary
 *   unknown | ordinary   | identifier | identifier | ordinary      | ordinary
 *
 * …and how a STRING on that engine escapes a character — every engine reads a
 * doubled closer (`'it''s'`), some read a backslash as well:
 *
 *   engine  | `\` escapes in `'…'` | `E'…'` escape string
 *   --------+----------------------+---------------------
 *   mysql   | YES (2)              | no
 *   pgsql   | no (3)               | YES
 *   others  | no                   | no
 *
 * …and whether a `/* … *\/` BLOCK COMMENT nests, which decides where it ends:
 *
 *   engine  | `/*a/*b*\/c*\/` is…
 *   --------+------------------------------------------------------------
 *   mssql   | ONE comment — T-SQL counts inner openers (documented)
 *   pgsql   | ONE comment — PostgreSQL counts inner openers (documented)
 *   mysql   | a comment `/*a/*b*\/` then the text `c*\/` — nesting is
 *           | explicitly NOT supported
 *   oracle  | same — "you cannot nest comments" (PL/SQL & SQL reference)
 *   sqlite  | same — a block comment ends at the first `*\/` (or end of input)
 *   kusto   | same, and moot: KQL has only `//` line comments
 *   unknown | same — see `UNKNOWN_ENGINE_POLICY`
 *
 *   (1) an IDENTIFIER when the session runs with `ANSI_QUOTES` — the caller
 *       passes `mysqlAnsiQuotes` (see `mysqlAnsiQuotesFromSqlMode`).
 *   (2) …unless the session runs with `NO_BACKSLASH_ESCAPES`, which makes the
 *       backslash ordinary — `mysqlNoBackslashEscapes`. It applies to the
 *       `"…"` string too, which is the same span with a different closer.
 *   (3) PostgreSQL has escaped plain strings only under
 *       `standard_conforming_strings = off`, off-by-default since 9.1 and NOT
 *       modelled: a server configured that way reads `'it\'s'` as one string
 *       where the masker closes it early. `E'…'` asks for the escaping
 *       explicitly and is modelled regardless of that setting.
 *
 * KNOWN GAP — KQL escapes with backslashes too (`'it\'s'`), and `@'…'` is its
 * verbatim form where they are literal. Neither is modelled: Kusto has no DDL
 * of the shape the callers classify, so the only cost is the conservative
 * direction the `standard_conforming_strings` row already takes. The grammar
 * scanner (`sql-grammar/document/codeProjection.ts`) has the same gap.
 *
 * The `$tag$` / `q'X…X'` columns are single-grammar forms — PostgreSQL dollar
 * quoting and Oracle's quote-delimiter literal — and stay OFF for the unknown
 * engine (see `UNKNOWN_ENGINE_POLICY`).
 */

import type { DatabaseEngineId } from '@/core/engines';

/**
 * One row of the module header's tables: how this engine's grammar reads the
 * constructs that are an identifier delimiter, a string literal, an escape, or
 * an ordinary character depending on the dialect. A `false` / absent flag
 * means the construct is an ORDINARY character on that engine.
 */
export type DelimiterPolicy = {
  /** `[…]` opens a delimited identifier (T-SQL, SQLite). */
  readonly bracketIdentifier: boolean;
  /** `"…"` is a delimited identifier (SQL standard) or a STRING to mask. */
  readonly doubleQuote: 'identifier' | 'string';
  /** `` `…` `` opens a delimited identifier (MySQL, SQLite). */
  readonly backtickIdentifier: boolean;
  /** `$tag$…$tag$` is a dollar-quoted STRING (PostgreSQL routine bodies). */
  readonly dollarQuotedString: boolean;
  /** `q'X…X'` / `nq'X…X'` (any case) is an Oracle quote-delimiter STRING. */
  readonly qQuotedString: boolean;
  /** A backslash escapes the next character inside a STRING (never inside a
   *  delimited identifier) — MySQL's default lexing. */
  readonly backslashEscapesStrings: boolean;
  /** `E'…'` / `e'…'` opens a STRING whose backslashes escape (PostgreSQL). */
  readonly escapeStringPrefix: boolean;
  /** A `/*` INSIDE a block comment opens a nested one, so the comment ends at
   *  the matching `*\/` rather than the first — T-SQL and PostgreSQL only. */
  readonly nestedBlockComments: boolean;
};

/** Every row is the SQL-standard baseline (`"…"` delimits an identifier,
 *  a doubled closer is the only escape, nothing else is special) plus that
 *  engine's own deviations — which keeps the WHY next to the difference. The
 *  module header has the full tables. */
const standardPolicy = (deviations: Partial<DelimiterPolicy>): DelimiterPolicy => ({
  bracketIdentifier: false,
  doubleQuote: 'identifier',
  backtickIdentifier: false,
  dollarQuotedString: false,
  qQuotedString: false,
  backslashEscapesStrings: false,
  escapeStringPrefix: false,
  nestedBlockComments: false,
  ...deviations,
});

/**
 * Used when the caller can't name the engine (`isSchemaDdl(sql)` with no
 * engine, a connection the store doesn't know). The decisions pull in
 * opposite directions, so each is made toward the safer failure:
 *  - `"…"` / `` `…` `` stay IDENTIFIERS. Every engine but MySQL agrees on
 *    `"`, and mis-reading an identifier as a string is the dangerous
 *    direction: an apostrophe inside `"a'b"` would open a string with no
 *    closer in sight and blank out the rest of the scan window, hiding a
 *    genuine `CREATE TABLE` later in the batch (a false NEGATIVE — stale
 *    caches, an index that still lists a dropped object).
 *  - `[…]` is NOT an identifier — i.e. mask MORE, not less. A PostgreSQL
 *    `ARRAY['DROP VIEW public.decoy']` must have its string masked; skipping
 *    that lets a value inside the array drive DDL classification. The cost is
 *    a false negative on a T-SQL `[a'b]` identifier, which is rarer than
 *    PostgreSQL arrays and than MySQL/Oracle code that puts `[` in a string.
 *  - `$…$` / `q'…'` / `E'…'` / backslash escapes stay OFF — see the module
 *    header: an unterminated one of those masks to the end of the window, the
 *    dangerous direction, and a backslash that is NOT an escape on the real
 *    engine would swallow a closer that really closes.
 *  - Block comments do NOT nest, for the same reason: reading `/*a/*b*\/` as
 *    still-open on an engine that closed it at the first `*\/` masks the rest
 *    of the window and hides the DDL after it. Four of the six engines do not
 *    nest, so this is also the majority reading.
 */
const UNKNOWN_ENGINE_POLICY: DelimiterPolicy = standardPolicy({ backtickIdentifier: true });

const ENGINE_DELIMITERS: Readonly<Record<DatabaseEngineId, DelimiterPolicy>> = {
  // T-SQL: `[a'b]` and (with the default QUOTED_IDENTIFIER ON) `"a'b"` are
  // both identifiers; a backtick has no meaning in the grammar, and a
  // backslash inside a string is an ordinary character.
  // …and T-SQL block comments NEST: `/*a/*b*\/c*\/` is one comment, so a
  // first-closer scan would leave `c*\/` wedged in the middle of the statement.
  mssql: standardPolicy({ bracketIdentifier: true, nestedBlockComments: true }),
  // PostgreSQL: `[` is ARRAY subscript / constructor syntax (`ARRAY['x']`,
  // `col[1]`), NEVER an identifier delimiter — treating it as one would hide
  // the string literal inside the array from the masker. `$$…$$` / `$tag$…$tag$`
  // IS a string (a routine body, a `DO` block), so a `DROP VIEW public.decoy`
  // spelled inside one must not drive DDL classification. `E'…'` is the
  // explicit escape-string form: inside it `\'` is an apostrophe, not the
  // closer, so `SELECT E'it\'s'; DROP VIEW v` must still show the DROP.
  // PostgreSQL nests block comments too ("/* and */ … can be nested").
  pgsql: standardPolicy({
    dollarQuotedString: true,
    escapeStringPrefix: true,
    nestedBlockComments: true,
  }),
  // MySQL: the backtick is THE identifier delimiter. `"…"` is a STRING
  // literal unless the session runs with `ANSI_QUOTES` (or the combination
  // mode `ANSI`), which is not the server default — so the row's default is
  // the string it is under the default config, and a caller that can observe
  // the session's `sql_mode` flips it via `MaskOptions.mysqlAnsiQuotes`. The
  // backslash escapes by default in BOTH string spellings (the same
  // `sql_mode`-driven lexing `security-management/mysqlSessionSqlMode.ts`
  // escapes passwords for), so `'it\'s'` is one string, not two.
  // MySQL documents that nested comments are NOT supported, so a block comment
  // ends at the first `*\/` — `/*a/*b*\/c*\/` is a comment followed by `c*\/`.
  mysql: standardPolicy({
    doubleQuote: 'string',
    backtickIdentifier: true,
    backslashEscapesStrings: true,
  }),
  // Oracle: `"…"` for identifiers; brackets and backticks are ordinary
  // characters. `q'[…]'` (and `nq'…'`) is the quote-delimiter string literal —
  // the form that exists precisely so a literal can contain apostrophes, which
  // is exactly what would otherwise open an unterminated string span here.
  // Oracle does not nest block comments either ("you cannot nest comments").
  oracle: standardPolicy({ qQuotedString: true }),
  // SQLite deliberately accepts all three spellings (`"…"` standard, `[…]`
  // from T-SQL, `` `…` `` from MySQL). Its block comment ends at the first
  // `*\/` or at the end of input — no nesting.
  sqlite: standardPolicy({ bracketIdentifier: true, backtickIdentifier: true }),
  // KQL: both `'…'` and `"…"` are string literals, and `['col name']` is a
  // bracketed STRING — so the bracket is an ordinary character and the quotes
  // inside it are masked as the string they are. Its backslash escapes are the
  // documented gap in the module header. Block-comment nesting is moot: KQL has
  // only `//` line comments.
  kusto: standardPolicy({ doubleQuote: 'string' }),
  // Azure Storage has no query language — never consulted; take the default.
  azurestorage: UNKNOWN_ENGINE_POLICY,
  // Mock engine (e2e harness): no real grammar of its own — take the default.
  mock: UNKNOWN_ENGINE_POLICY,
};

export type MaskOptions = {
  /** Engine whose delimiter grammar applies. Omit only when the caller
   *  genuinely cannot know it — see `UNKNOWN_ENGINE_POLICY`. */
  readonly engine?: DatabaseEngineId;
  /** MySQL only: the session runs with `ANSI_QUOTES`, which makes `"…"` an
   *  IDENTIFIER instead of a string literal. Ignored on every other engine.
   *  Omitted / `false` keeps the MySQL server default (`"…"` is a string), so
   *  existing callers are unaffected. Derive it from the session's `sql_mode`
   *  with `mysqlAnsiQuotesFromSqlMode` — it IS observable (the MySQL
   *  IntelliSense snapshot carries `sqlMode`); without it a successful
   *  `DROP VIEW "v"` under `ANSI_QUOTES` has its target blanked and the
   *  dropped view stays indexed. */
  readonly mysqlAnsiQuotes?: boolean;
  /** MySQL only: the session runs with `NO_BACKSLASH_ESCAPES`, which makes the
   *  backslash an ordinary character inside a string. Same source, same
   *  default-safe omission as `mysqlAnsiQuotes`. */
  readonly mysqlNoBackslashEscapes?: boolean;
};

/** The row `options` selects, with the deviations the TABLE cannot carry
 *  because they are session settings rather than grammar. */
export function policyFor(options?: MaskOptions): DelimiterPolicy {
  const base = options?.engine ? ENGINE_DELIMITERS[options.engine] : UNKNOWN_ENGINE_POLICY;
  if (options?.engine !== 'mysql') return base;
  // Under `ANSI_QUOTES` MySQL reads `"…"` exactly like every other engine, and
  // under `NO_BACKSLASH_ESCAPES` a backslash is just a character.
  const ansiQuotes = options.mysqlAnsiQuotes === true;
  const noBackslashEscapes = options.mysqlNoBackslashEscapes === true;
  if (!ansiQuotes && !noBackslashEscapes) return base;
  return {
    ...base,
    doubleQuote: ansiQuotes ? 'identifier' : base.doubleQuote,
    backslashEscapesStrings: noBackslashEscapes ? false : base.backslashEscapesStrings,
  };
}
